import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { 
  ShieldAlert, 
  ArrowRight, 
  ArrowLeft,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Shield,
  TrendingUp,
  Target,
  PieChart,
  Sparkles,
  Award,
  Zap,
  AlertTriangle,
  ChevronRight,
  BarChart3,
  BookOpen,
  Calculator,
  Users,
  Clock,
  Wallet
} from 'lucide-react';
import Layout from '../components/Layout';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Quiz() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { token } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [animatingOption, setAnimatingOption] = useState(null);

  const fetchQuestions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/quiz/questions`);
      setQuestions(response.data);
    } catch (error) {
      toast.error(t('Failed to load quiz', 'Imeshindwa kupakia mtihani'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const handleAnswer = (questionId, value) => {
    setAnimatingOption(value);
    setTimeout(() => setAnimatingOption(null), 300);
    
    setAnswers(prev => {
      const existing = prev.findIndex(a => a.question_id === questionId);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = { question_id: questionId, value };
        return updated;
      }
      return [...prev, { question_id: questionId, value }];
    });
  };

  const getSelectedValue = (questionId) => {
    const answer = answers.find(a => a.question_id === questionId);
    return answer?.value;
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (answers.length < questions.length) {
      toast.error(t('Please answer all questions', 'Tafadhali jibu maswali yote'));
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/quiz/submit`, { answers }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResult(response.data);
    } catch (error) {
      toast.error(t('Failed to submit quiz', 'Imeshindwa kutuma mtihani'));
    } finally {
      setSubmitting(false);
    }
  };

  const resetQuiz = () => {
    setAnswers([]);
    setCurrentIndex(0);
    setResult(null);
  };

  // Enhanced risk level configuration
  const getRiskConfig = (level) => {
    const configs = {
      'Conservative': {
        color: 'from-emerald-500 to-emerald-600',
        bgColor: 'bg-emerald-500/10',
        textColor: 'text-emerald-500',
        borderColor: 'border-emerald-500/30',
        icon: Shield,
        score: 1,
        allocation: { bonds: 60, tbills: 25, stocks: 10, utt: 5 },
        traits: language === 'sw' 
          ? ['Unapendelea usalama', 'Uvumilivu wa hatari mdogo', 'Malengo ya muda mfupi']
          : ['Safety-first mindset', 'Low risk tolerance', 'Short-term goals'],
        suggestion: language === 'sw'
          ? 'Anza na Treasury Bills na Government Bonds. Ni salama na una uhakika wa faida.'
          : 'Start with Treasury Bills and Government Bonds. They are safe with guaranteed returns.'
      },
      'Moderate-Conservative': {
        color: 'from-blue-500 to-blue-600',
        bgColor: 'bg-blue-500/10',
        textColor: 'text-blue-500',
        borderColor: 'border-blue-500/30',
        icon: Target,
        score: 2,
        allocation: { bonds: 45, tbills: 20, stocks: 20, utt: 15 },
        traits: language === 'sw' 
          ? ['Wastani wa hatari', 'Malengo ya muda wa kati', 'Unapenda usawa']
          : ['Cautiously optimistic', 'Medium-term goals', 'Balance seeker'],
        suggestion: language === 'sw'
          ? 'Changanya Bonds na UTT Funds. Ongeza hisa kidogo kwa ukuaji.'
          : 'Mix Bonds with UTT Funds. Add some stocks for growth potential.'
      },
      'Moderate': {
        color: 'from-amber-500 to-amber-600',
        bgColor: 'bg-amber-500/10',
        textColor: 'text-amber-500',
        borderColor: 'border-amber-500/30',
        icon: BarChart3,
        score: 3,
        allocation: { stocks: 35, utt: 30, bonds: 25, tbills: 10 },
        traits: language === 'sw' 
          ? ['Usawa mzuri', 'Malengo ya muda mrefu', 'Uvumilivu wastani']
          : ['Balanced approach', 'Long-term vision', 'Moderate risk tolerance'],
        suggestion: language === 'sw'
          ? 'Usawa wa Hisa, UTT na Bonds ni mkakati bora kwako.'
          : 'A balanced mix of Stocks, UTT Funds, and Bonds is ideal for you.'
      },
      'Moderate-Aggressive': {
        color: 'from-orange-500 to-orange-600',
        bgColor: 'bg-orange-500/10',
        textColor: 'text-orange-500',
        borderColor: 'border-orange-500/30',
        icon: TrendingUp,
        score: 4,
        allocation: { stocks: 50, utt: 25, etfs: 15, bonds: 10 },
        traits: language === 'sw' 
          ? ['Unatafuta ukuaji', 'Uvumilivu wa hatari juu', 'Mtazamo wa muda mrefu']
          : ['Growth-focused', 'Higher risk tolerance', 'Long-term horizon'],
        suggestion: language === 'sw'
          ? 'Zingatia hisa za DSE na UTT Growth Funds. Una wakati wa kutosha.'
          : 'Focus on DSE stocks and UTT Growth Funds. Time is on your side.'
      },
      'Aggressive': {
        color: 'from-red-500 to-red-600',
        bgColor: 'bg-red-500/10',
        textColor: 'text-red-500',
        borderColor: 'border-red-500/30',
        icon: Zap,
        score: 5,
        allocation: { stocks: 70, etfs: 15, utt: 10, bonds: 5 },
        traits: language === 'sw' 
          ? ['Unatafuta faida kubwa', 'Uvumilivu mkubwa wa hatari', 'Mwekezaji mwenye ujuzi']
          : ['Maximum growth seeker', 'High risk appetite', 'Experienced investor'],
        suggestion: language === 'sw'
          ? 'Hisa za DSE ndiyo njia yako. Lakini diversify kwa usalama.'
          : 'DSE stocks are your path. But diversify for safety.'
      }
    };
    return configs[level] || configs['Moderate'];
  };

  const progress = ((currentIndex + 1) / questions.length) * 100;
  const currentQuestion = questions[currentIndex];

  if (loading) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center animate-pulse">
            <ShieldAlert className="w-8 h-8 text-primary" />
          </div>
          <p className="text-muted-foreground">{t('Loading assessment...', 'Inapakia tathmini...')}</p>
        </div>
      </Layout>
    );
  }

  // RESULTS PAGE
  if (result) {
    const riskConfig = getRiskConfig(result.risk_level);
    const RiskIcon = riskConfig.icon;

    return (
      <Layout>
        <div className="page-enter max-w-4xl mx-auto space-y-6 pb-8">
          {/* Result Header */}
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-background via-background to-muted/20 border border-border/50 p-6 md:p-8">
            <div className={`absolute top-0 right-0 w-96 h-96 bg-gradient-to-br ${riskConfig.color} opacity-10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2`} />
            
            <div className="relative flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className={`w-20 h-20 md:w-24 md:h-24 rounded-3xl bg-gradient-to-br ${riskConfig.color} flex items-center justify-center shadow-xl shadow-current/20`}>
                <RiskIcon className="w-10 h-10 md:w-12 md:h-12 text-white" />
              </div>
              <div className="flex-1">
                <Badge className={`${riskConfig.bgColor} ${riskConfig.textColor} border-0 mb-2`}>
                  <Award className="w-3 h-3 mr-1" />
                  {t('Your Risk Profile', 'Wasifu Wako wa Hatari')}
                </Badge>
                <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2" data-testid="risk-level-result">
                  {language === 'sw' ? result.risk_level_sw : result.risk_level}
                </h1>
                <p className="text-muted-foreground max-w-lg">
                  {language === 'sw' ? result.description_sw : result.description_en}
                </p>
              </div>
            </div>

            {/* Risk Score Visual */}
            <div className="mt-8 relative">
              <div className="flex justify-between items-center mb-2 text-xs text-muted-foreground">
                <span>{t('Conservative', 'Salama')}</span>
                <span>{t('Aggressive', 'Mkali')}</span>
              </div>
              <div className="h-3 bg-gradient-to-r from-emerald-500 via-amber-500 to-red-500 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-transparent relative"
                  style={{ width: `${(riskConfig.score / 5) * 100}%` }}
                >
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-5 h-5 bg-white rounded-full shadow-lg border-2 border-foreground" />
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Traits Card */}
            <Card className={`${riskConfig.borderColor} border-2 ${riskConfig.bgColor}`}>
              <CardContent className="p-6">
                <h3 className={`font-semibold ${riskConfig.textColor} mb-4 flex items-center gap-2`}>
                  <Users className="w-4 h-4" />
                  {t('Your Investor Profile', 'Wasifu Wako wa Mwekezaji')}
                </h3>
                <div className="space-y-3">
                  {riskConfig.traits.map((trait, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle2 className={`w-5 h-5 ${riskConfig.textColor} flex-shrink-0`} />
                      <span className="text-foreground">{trait}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Recommended Allocation */}
            <Card className="border-border/50">
              <CardContent className="p-6">
                <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                  <PieChart className="w-4 h-4 text-primary" />
                  {t('Recommended Allocation', 'Mgawanyo Unaopendekezwa')}
                </h3>
                <div className="space-y-3">
                  {Object.entries(riskConfig.allocation).map(([type, percent]) => {
                    const typeNames = {
                      stocks: language === 'sw' ? 'Hisa za DSE' : 'DSE Stocks',
                      bonds: language === 'sw' ? 'Hatifungani' : 'Government Bonds',
                      tbills: language === 'sw' ? 'T-Bills' : 'Treasury Bills',
                      utt: language === 'sw' ? 'UTT Funds' : 'UTT Funds',
                      etfs: 'ETFs'
                    };
                    return (
                      <div key={type} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{typeNames[type]}</span>
                          <span className="font-semibold text-foreground">{percent}%</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className={`h-full bg-gradient-to-r ${riskConfig.color} rounded-full transition-all duration-500`}
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Expert Suggestion */}
          <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                    {t('AI Coach Recommendation', 'Pendekezo la Mshauri wa AI')}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {riskConfig.suggestion}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recommended Investments */}
          <Card className="border-border/50">
            <CardContent className="p-6">
              <h3 className="font-semibold text-foreground mb-4">
                {t('Best Investments For You', 'Uwekezaji Bora Kwako')}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {result.recommended_investments.map((inv, i) => {
                  const invNames = {
                    treasury_bills: { icon: Wallet, name: 'Treasury Bills', nameSw: 'T-Bills' },
                    government_bonds: { icon: Shield, name: 'Bonds', nameSw: 'Hatifungani' },
                    bonds: { icon: Shield, name: 'Bonds', nameSw: 'Hatifungani' },
                    utt_funds: { icon: PieChart, name: 'UTT Funds', nameSw: 'UTT' },
                    utt: { icon: PieChart, name: 'UTT Funds', nameSw: 'UTT' },
                    stocks: { icon: TrendingUp, name: 'Stocks', nameSw: 'Hisa' },
                    etfs: { icon: BarChart3, name: 'ETFs', nameSw: 'ETFs' }
                  };
                  const config = invNames[inv] || { icon: Target, name: inv, nameSw: inv };
                  const Icon = config.icon;
                  return (
                    <button
                      key={i}
                      onClick={() => navigate('/simulator')}
                      className="p-4 rounded-xl border border-border/50 bg-muted/30 hover:bg-muted hover:border-primary/30 transition-all duration-200 text-center group"
                    >
                      <Icon className="w-8 h-8 mx-auto mb-2 text-primary group-hover:scale-110 transition-transform" />
                      <span className="text-sm font-medium text-foreground">
                        {language === 'sw' ? config.nameSw : config.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="grid sm:grid-cols-3 gap-3">
            <Button 
              onClick={() => navigate('/simulator')}
              className="h-14 rounded-xl bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg shadow-primary/20"
              data-testid="go-to-simulator-btn"
            >
              <Calculator className="w-5 h-5 mr-2" />
              {t('Simulate Returns', 'Hesabu Faida')}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/chat')}
              className="h-14 rounded-xl"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              {t('Ask AI Coach', 'Uliza Mshauri')}
            </Button>
            <Button 
              variant="ghost"
              onClick={resetQuiz}
              className="h-14 rounded-xl"
            >
              <RefreshCw className="w-5 h-5 mr-2" />
              {t('Retake Quiz', 'Rudia')}
            </Button>
          </div>

          {/* Credibility Badge */}
          <div className="flex items-center justify-center gap-3 text-xs text-muted-foreground pt-4">
            <Badge variant="outline" className="py-1.5">
              <ShieldAlert className="w-3 h-3 mr-1" />
              {t('Based on Modern Portfolio Theory', 'Kwa Nadharia ya Kisasa ya Portfolio')}
            </Badge>
            <Badge variant="outline" className="py-1.5">
              <Users className="w-3 h-3 mr-1" />
              {t('10,000+ Assessments', 'Tathmini 10,000+')}
            </Badge>
          </div>
        </div>
      </Layout>
    );
  }

  // QUIZ PAGE
  return (
    <Layout>
      <div className="page-enter max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center mx-auto mb-4">
            <ShieldAlert className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
            {t('Risk Profile Assessment', 'Tathmini ya Wasifu wa Hatari')}
          </h1>
          <p className="text-muted-foreground max-w-md mx-auto">
            {t('Answer honestly to get personalized investment recommendations.', 'Jibu kwa uaminifu kupata mapendekezo ya uwekezaji yanayokufaa.')}
          </p>
        </div>

        {/* Progress */}
        <Card className="border-border/50 bg-muted/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <span className="text-sm font-bold text-primary">{currentIndex + 1}</span>
                </div>
                <span className="text-sm text-muted-foreground">
                  {t('of', 'ya')} {questions.length} {t('questions', 'maswali')}
                </span>
              </div>
              <Badge variant="outline" className="py-1">
                <Clock className="w-3 h-3 mr-1" />
                ~2 {t('min', 'dak')}
              </Badge>
            </div>
            <Progress value={progress} className="h-2" />
          </CardContent>
        </Card>

        {/* Question Card */}
        {currentQuestion && (
          <Card className="border-border/50 overflow-hidden">
            <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-6 border-b border-border/50">
              <h2 className="text-lg font-semibold text-foreground">
                {language === 'sw' ? currentQuestion.question_sw : currentQuestion.question_en}
              </h2>
            </div>
            <CardContent className="p-6">
              <div className="space-y-3">
                {currentQuestion.options.map((option, i) => {
                  const isSelected = getSelectedValue(currentQuestion.id) === option.value;
                  const isAnimating = animatingOption === option.value;
                  return (
                    <button
                      key={i}
                      onClick={() => handleAnswer(currentQuestion.id, option.value)}
                      className={`w-full p-4 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${
                        isSelected 
                          ? 'border-primary bg-primary/5 shadow-md shadow-primary/10' 
                          : 'border-border hover:border-primary/30 hover:bg-muted/30'
                      } ${isAnimating ? 'scale-[0.98]' : ''}`}
                      data-testid={`quiz-option-${i}`}
                    >
                      <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                        isSelected 
                          ? 'border-primary bg-primary' 
                          : 'border-muted-foreground'
                      }`}>
                        {isSelected && <CheckCircle2 className="w-5 h-5 text-primary-foreground" />}
                      </div>
                      <span className={`flex-1 ${isSelected ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                        {language === 'sw' ? option.text_sw : option.text_en}
                      </span>
                      {isSelected && (
                        <Badge className="bg-primary/20 text-primary border-0">
                          {t('Selected', 'Imechaguliwa')}
                        </Badge>
                      )}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            className="rounded-xl"
            data-testid="quiz-prev-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('Previous', 'Nyuma')}
          </Button>

          {currentIndex === questions.length - 1 ? (
            <Button
              onClick={handleSubmit}
              disabled={submitting || answers.length < questions.length}
              className="rounded-xl bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg shadow-primary/20"
              data-testid="quiz-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('Analyzing...', 'Inachambua...')}
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  {t('Get My Profile', 'Pata Wasifu Wangu')}
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              disabled={!getSelectedValue(currentQuestion?.id)}
              className="rounded-xl"
              data-testid="quiz-next-btn"
            >
              {t('Next', 'Endelea')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>

        {/* Trust Indicators */}
        <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground pt-4">
          <span className="flex items-center gap-1">
            <Shield className="w-3 h-3" />
            {t('Secure & Private', 'Salama na Siri')}
          </span>
          <span className="flex items-center gap-1">
            <Sparkles className="w-3 h-3" />
            {t('AI-Powered', 'Inatumia AI')}
          </span>
          <span className="flex items-center gap-1">
            <Award className="w-3 h-3" />
            {t('CMSA Guidelines', 'Miongozo ya CMSA')}
          </span>
        </div>
      </div>
    </Layout>
  );
}
